import { motion } from 'motion/react';
import type { StateSnapshot } from '@shared/types';
import { PRE_ROLL_MS } from '@shared/engine';
import { INTROS, difficultyLabel, pick } from '@/lib/kudos';
import { serverNow } from '@/lib/api';
import { Chip } from '@/components/Chip';
import { Stamp } from '@/components/Stamp';
import { Pulse } from '@/brand/Pulse';
import { RoundCard } from '@/components/RoundCard';
import { COPY, formatName } from './copy';
import type { LiveQuestion } from '@/formats/types';
import { useCalm } from './time';

export type IntroStamp = 'double' | 'lightning' | 'final';
/** Which stamp the intro card carries: double points beats lightning beats the final question. */
export function stampFor(q: Pick<LiveQuestion, 'multiplier' | 'tags'>, index: number, total: number): IntroStamp | undefined {
  if ((q.multiplier ?? 1) > 1 || q.tags?.includes('double')) return 'double';
  if (q.tags?.includes('lightning')) return 'lightning';
  if (index === total - 1 || q.tags?.includes('final')) return 'final';
  return undefined;
}
/** The intro line under the stamp: the final question's line wins, so a doubled finale says what is at stake. */
export function introLineFor(stamp: IntroStamp | undefined, isFinal: boolean, seed: string): string | undefined {
  if (isFinal) return pick(INTROS.final, seed);
  if (stamp === 'double') return pick(INTROS.double, seed);
  if (stamp === 'lightning') return pick(INTROS.lightning, seed);
  return undefined;
}

const STAMP_TEXT: Record<IntroStamp, string> = { double: COPY.question.double, lightning: COPY.question.lightning, final: COPY.question.final };

type Hinted = Pick<LiveQuestion, 'format' | 'graphic'> & Partial<Pick<LiveQuestion, 'hotspot' | 'multi'>>;
/**
 * The line under the format's name: how the format works. The question screen that follows no longer explains it (number,
 * order and pick-all carry only their Lock in; the others give the one-tap rule), so this is where it is said, once, and
 * never as the name again in other words. In classroom mode it says where the question is.
 */
export function introHint(q: Hinted, screen = false): string {
  if (screen) return 'The question is on the big screen. Tap your answer here.';
  switch (q.format) {
    case 'tf': return 'One statement. Your call.';
    case 'spot': return 'Abnormal is not always guilty.';
    case 'clues': return 'A clue every few seconds. Answer early for more points.';
    case 'gmcq': return q.graphic?.type === 'ecg' ? 'The answer is in the trace.' : 'The answer is in the numbers.';
    case 'number': return 'Type it on the pad, then lock in.';
    case 'hotspot': return q.hotspot?.target === 'ecg-lead' ? 'The ECG is your answer sheet.' : 'The monitor is your answer sheet.';
    case 'order': return 'Tap each step in turn, then lock in.';
    case 'multi': return q.multi?.mode === 'rarity' ? 'Several answers are right. Any one of them scores.' : 'Tap each one that fits, then lock in.';
    default: return 'Read to the end. The detail decides it.';
  }
}

/** True when this question opens a themed round: its pre-roll shows the round's rule card instead of the intro card. */
export const opensRound = (q: Pick<LiveQuestion, 'round'>) => Boolean(q.round?.first);

/**
 * M3: the pre-roll card. Question number in big numerals, what kind of question it is, and any stamp. A question that opens
 * a themed round shows the round's rule card instead, timed from the same server moment; the question number and the
 * format follow on the question screen a moment later, so the round card does not say them first.
 */
export function IntroCard({ snap, q, playerId }: { snap: StateSnapshot; q: LiveQuestion; playerId?: string }) {
  if (q.round && opensRound(q)) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]">
        <RoundCard round={q.round} startsAt={q.startsAt} />
      </div>
    );
  }
  return <QuestionIntro snap={snap} q={q} playerId={playerId} />;
}

function QuestionIntro({ snap, q, playerId }: { snap: StateSnapshot; q: LiveQuestion; playerId?: string }) {
  const reduce = useCalm();
  const isFinal = snap.index === snap.total - 1;
  const stamp = stampFor(q, snap.index, snap.total);
  const line = introLineFor(stamp, isFinal, `${playerId ?? ''}:${snap.index}:intro`);
  const level = difficultyLabel(q.difficulty);
  const remaining = Math.max(0, q.startsAt - serverNow());
  const done = 1 - Math.min(1, remaining / PRE_ROLL_MS);
  const screen = snap.display === 'screen';
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]">
      <motion.section aria-label={COPY.question.of(snap.index, snap.total)} data-stamp={stamp ?? ''}
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 64, rotate: -2, scale: 0.94 }} animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className="relative flex flex-col gap-4 overflow-hidden rounded-card bg-surface p-6 shadow-pop">
        <div className="-mx-6 -mt-6 mb-1 bg-brand-tint px-6 pb-2 pt-4 text-brand">
          <Pulse mood="steady" width="100%" height={34} />
        </div>
        <p className="flex items-end gap-2 leading-none">
          <span className="sr-only">{COPY.question.of(snap.index, snap.total)}</span>
          <span aria-hidden="true" className="numeral text-[7.5rem] leading-[0.8] tracking-tight text-ink">{snap.index + 1}</span>
          <span aria-hidden="true" className="numeral pb-1 text-3xl text-ink-3">/{snap.total}</span>
        </p>
        {(q.category || level) && (
          <div className="flex flex-wrap gap-2">
            {q.category && <Chip tone="brand">{q.category}</Chip>}
            {level && <Chip tone={q.difficulty === 'hard' ? 'rose' : q.difficulty === 'easy' ? 'emerald' : 'slate'}>{level}</Chip>}
          </div>
        )}
        <div>
          <h1 className="font-display text-[2rem] font-extrabold leading-tight tracking-tight text-ink">{formatName(q)}</h1>
          <p className="mt-1 text-ink-2">{introHint(q, screen)}</p>
        </div>
        {stamp && (
          // Slammed on as the card settles; double points and lightning buzz with the defib charge afterwards.
          <p data-testid="intro-stamp" className="absolute right-5 top-[5.5rem]">
            <Stamp tone={stamp === 'lightning' ? 'iv' : 'cart'} tilt={-6} charge={stamp !== 'final'} className="text-lg">{STAMP_TEXT[stamp]}</Stamp>
          </p>
        )}
        {line && <p className="text-2xl leading-snug text-ink hand">{line}</p>}
        <div className="mt-1 h-1.5 overflow-hidden bg-surface-2" aria-hidden="true">
          <motion.div className="h-full origin-left bg-brand" initial={{ scaleX: done }} animate={{ scaleX: 1 }} transition={{ duration: remaining / 1000, ease: 'linear' }} />
        </div>
      </motion.section>
    </div>
  );
}
